import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowLeft,
  MapPin,
  Briefcase,
  Building2,
  Users,
  Calendar,
  Sparkles,
  Check,
} from "lucide-react";
import { getJob } from "@/lib/api";
import { useCandidate } from "@/lib/candidate";
import { scoreJob, matchBadge } from "@/lib/jobMatch";
import { jobPostingSchema, breadcrumbSchema } from "@/lib/schemas";
import Seo from "@/components/Seo";

const EASE = [0.76, 0, 0.24, 1];

const formatSalary = (min, max) => {
  const fmt = (n) => {
    if (n == null) return "";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1).replace(/\.0$/, "")} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(0)} L`;
    return `₹${n}`;
  };
  if (min && max) return `${fmt(min)} — ${fmt(max)}`;
  if (min) return `${fmt(min)}+`;
  if (max) return `Up to ${fmt(max)}`;
  return "Competitive";
};

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { parsedCV } = useCandidate();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setJob(null);
    setNotFound(false);
    getJob(id)
      .then(setJob)
      .catch(() => setNotFound(true));
  }, [id]);

  const match = job && parsedCV?.skills?.length
    ? scoreJob(parsedCV.skills, job.tech_stack || [])
    : null;
  const badge = match ? matchBadge(match.score) : null;
  const matchedSet = new Set((match?.matched || []).map((s) => s.toLowerCase()));

  if (notFound) {
    return (
      <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24 flex items-center justify-center">
        <Seo title="Role not found" noIndex path={`/jobs/${id}`} />
        <div className="text-center">
          <p className="font-serif-accent italic text-[var(--accent)] text-2xl mb-2">
            This role has closed.
          </p>
          <Link to="/jobs" className="btn-pill mt-6">
            <span>Back to all roles</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-24 bg-[var(--bg-2)]" />
            <div className="h-12 w-2/3 bg-[var(--bg-2)]" />
            <div className="h-4 w-1/2 bg-[var(--bg-2)]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24" data-testid="job-detail-page">
      <Seo
        title={`${job.title} at ${job.company}`}
        description={`${job.title} · ${job.company} · ${job.location} · ${job.remote_type}. ${job.description.slice(0, 150)}`}
        path={`/jobs/${job.id}`}
        type="article"
        jsonLd={[
          jobPostingSchema(job),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Jobs", path: "/jobs" },
            { name: job.title, path: `/jobs/${job.id}` },
          ]),
        ]}
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <button
          onClick={() => navigate("/jobs")}
          className="link-slide inline-flex items-center gap-2 text-sm uppercase tracking-[0.22em] text-[var(--ink-70)] mb-10"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>All roles</span>
        </button>

        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] border border-[var(--accent)]/40 px-2 py-1">
                {job.industry}
              </span>
              {badge && (
                <span
                  data-testid="job-detail-match"
                  className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border inline-flex items-center gap-1.5 ${
                    badge.tone === "strong"
                      ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                      : "border-[var(--line-strong)] text-[var(--ink)]"
                  }`}
                >
                  <Sparkles className="w-3 h-3" strokeWidth={2} />
                  {match.score}% · {badge.label}
                </span>
              )}
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[0.95] text-[var(--ink)] mb-6"
            >
              {job.title}
            </motion.h1>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[var(--ink-80)] text-base mb-10">
              <span className="inline-flex items-center gap-2">
                <Building2 className="w-4 h-4 text-[var(--ink-50)]" /> {job.company}
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[var(--ink-50)]" /> {job.location} · {job.remote_type}
              </span>
              <span className="inline-flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[var(--ink-50)]" /> {job.seniority}
              </span>
              <span className="inline-flex items-center gap-2">
                <Users className="w-4 h-4 text-[var(--ink-50)]" /> {job.openings} opening{job.openings === 1 ? "" : "s"}
              </span>
            </div>

            <div className="border-t border-[var(--line-strong)] pt-10">
              <p className="overline mb-4">About the role</p>
              <p className="font-body text-lg leading-relaxed text-[var(--ink)] whitespace-pre-wrap max-w-3xl">
                {job.description}
              </p>
            </div>

            {job.tech_stack?.length > 0 && (
              <div className="mt-12 border-t border-[var(--line-strong)] pt-10">
                <p className="overline mb-4">Tech stack</p>
                <div className="flex flex-wrap gap-2">
                  {job.tech_stack.map((t) => {
                    const isMatched = matchedSet.has(t.toLowerCase());
                    return (
                      <span
                        key={t}
                        className={`text-sm px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${
                          isMatched
                            ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/5"
                            : "border-[var(--line-strong)] text-[var(--ink)]"
                        }`}
                      >
                        {isMatched && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
                        {t}
                      </span>
                    );
                  })}
                </div>
                {match?.missing?.length > 0 && (
                  <p className="mt-6 text-sm text-[var(--ink-70)]">
                    <span className="text-[var(--ink-50)] uppercase tracking-[0.18em] text-[10px] mr-2">
                      To level up
                    </span>
                    {match.missing.join(" · ")}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Sticky apply column */}
          <aside className="md:col-span-4 md:sticky md:top-24 md:self-start space-y-6">
            <div className="border border-[var(--line-strong)] bg-[var(--bg-2)] p-6">
              <p className="overline mb-3">Compensation</p>
              <p className="font-display text-3xl text-[var(--ink)] mb-6">
                {formatSalary(job.salary_min, job.salary_max)}
              </p>
              <Link
                to={`/apply?job=${job.id}`}
                className="btn-pill w-full justify-center"
                data-testid="job-detail-apply"
              >
                <span>Apply for this role</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <div className="mt-6 pt-6 border-t border-[var(--line-strong)] space-y-2 text-sm text-[var(--ink-80)]">
                <p className="inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[var(--ink-50)]" />
                  Posted{" "}
                  {new Date(job.posted_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
                <p className="text-[var(--ink-50)] text-xs uppercase tracking-[0.22em]">
                  {job.employment_type}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
